import { useRef, useCallback } from "react";

const GESTURE_DEADZONE = 10;
const HORIZONTAL_LOCK_THRESHOLD = 14;
const VERTICAL_LOCK_THRESHOLD = 14;
const SWIPE_TRIGGER_THRESHOLD = 60;
const MAX_SHIFT = 56;
const MOBILE_BREAKPOINT = 767;

export function useSwipeReply(onReply) {
    const touchStartX = useRef(null);
    const touchStartY = useRef(null);
    const swipeEl = useRef(null);
    const gestureState = useRef("idle"); // idle | undecided | horizontal | vertical

    const isMobile = () => window.innerWidth <= MOBILE_BREAKPOINT;

    const resetSwipeState = useCallback(() => {
        if (swipeEl.current?.el) {
            swipeEl.current.el.style.transform = "translateX(0)";
            swipeEl.current.el.style.transition = "transform 0.25s ease";
        }
        touchStartX.current = null;
        touchStartY.current = null;
        swipeEl.current = null;
        gestureState.current = "idle";
    }, []);

    const onTouchStart = useCallback(
        (e, msg) => {
            if (!isMobile()) return;
            touchStartX.current = e.touches[0].clientX;
            touchStartY.current = e.touches[0].clientY;
            swipeEl.current = { msg, el: e.currentTarget };
            gestureState.current = "undecided";
        },
        []
    );

    const onTouchMove = useCallback((e) => {
        if (!isMobile() || touchStartX.current === null) return;
        const dx = touchStartX.current - e.touches[0].clientX;
        const absDx = Math.abs(dx);
        const absDy = Math.abs(touchStartY.current - e.touches[0].clientY);

        if (gestureState.current === "undecided") {
            if (absDx < GESTURE_DEADZONE && absDy < GESTURE_DEADZONE) return;
            if (dx > 0 && absDx > absDy && absDx >= HORIZONTAL_LOCK_THRESHOLD) {
                gestureState.current = "horizontal";
            } else if (absDy >= VERTICAL_LOCK_THRESHOLD && absDy >= absDx) {
                gestureState.current = "vertical";
            } else {
                return;
            }
        }

        if (gestureState.current === "vertical") return;

        if (gestureState.current === "horizontal" && swipeEl.current?.el) {
            // Once horizontal intent is confirmed, block vertical scrolling.
            e.preventDefault();
            const shift = Math.min(Math.max(dx, 0), MAX_SHIFT);
            swipeEl.current.el.style.transform = `translateX(-${shift}px)`;
            swipeEl.current.el.style.transition = "none";
        }
    }, []);

    const onTouchEnd = useCallback(
        (e) => {
            if (!isMobile() || touchStartX.current === null) return;
            const dx = touchStartX.current - e.changedTouches[0].clientX;
            if (
                gestureState.current === "horizontal" &&
                dx >= SWIPE_TRIGGER_THRESHOLD &&
                swipeEl.current?.msg
            ) {
                onReply(swipeEl.current.msg);
            }
            resetSwipeState();
        },
        [onReply, resetSwipeState]
    );

    return { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel: resetSwipeState };
}